﻿import React, { useState, useEffect } from "react";
import { getInsiders } from "../../utils/companyApi.js";
import { fmtMoney, fmtNumber, fmtPct, fmtLongDate } from "../../utils/format.js";
import { Loading, SubTabs } from "./common.jsx";

const CODES = {
  P: "Buy", S: "Sell", A: "Award", M: "Option exercise", X: "Option exercise", F: "Tax withholding",
  G: "Gift", D: "Sold to company", C: "Conversion", I: "Discretionary", J: "Other", W: "Inheritance",
};
const day = (s) => Date.parse(`${s}T00:00:00Z`);
const amount = (t) => (t.shares || 0) * (t.price || 0);

export default function OwnershipTab({ ticker }) {
  const [view, setView] = useState("holders");
  const [state, setState] = useState({ data: null, loading: true, error: "" });

  // the first visit to a company reads its filings from the SEC, so keep checking until it is done
  useEffect(() => {
    let dead = false;
    let timer = null;
    setState({ data: null, loading: true, error: "" });
    const load = () =>
      getInsiders(ticker)
        .then((d) => {
          if (dead) return;
          if (d.error) return setState({ data: null, loading: false, error: "No insider data for this company." });
          setState({ data: d, loading: false, error: "" });
          if (d.syncing) timer = setTimeout(load, 3000);
        })
        .catch((e) => !dead && setState({ data: null, loading: false, error: e.message }));
    load();
    return () => {
      dead = true;
      clearTimeout(timer);
    };
  }, [ticker]);

  const { data, loading, error } = state;
  if (loading) return <Loading />;
  if (error) return <div className="card co-card"><p className="co-hint">{error}</p></div>;

  const year = Date.now() - 365 * 864e5;
  const recent = data.trades.filter((t) => day(t.date) >= year);
  const buys = recent.filter((t) => t.code === "P");
  const sells = recent.filter((t) => t.code === "S");
  const sum = (a) => a.reduce((s, t) => s + amount(t), 0);
  const so = data.sharesOutstandingMM ? data.sharesOutstandingMM * 1e6 : null;
  const empty = !data.trades.length && !data.holdings.length;

  return (
    <div>
      <SubTabs
        items={[["holders", "Insider Holdings"], ["insiders", "Insider Transactions"]]}
        value={view}
        onChange={setView}
      />

      {data.syncing && <p className="co-hint">Reading this company's insider filings from the SEC… this takes about 10 seconds the first time.</p>}
      {empty && !data.syncing && <div className="card co-card"><p className="co-hint">No insider filings (SEC Form 4) found in the last 13 months.</p></div>}

      {!empty && (
        <p className="co-hint">
          Last 12 months, open-market: {buys.length} buys ({fmtMoney(sum(buys), 0)}) and {sells.length} sells ({fmtMoney(sum(sells), 0)}).
        </p>
      )}

      {!empty && view === "holders" && (
        <div className="card co-card co-table-wrap">
          <table className="co-table co-table-left">
            <thead>
              <tr><th>Holder</th><th>Role</th><th>Security</th><th>Held</th><th>Shares</th><th>% of shares out.</th><th>Value ($)</th><th>As of</th></tr>
            </thead>
            <tbody>
              {data.holdings.map((h, i) => {
                const pct = so && /common|ordinary/i.test(h.security) ? (h.shares / so) * 100 : null;
                return (
                  <tr key={i}>
                    <td>{h.insider}</td>
                    <td>{h.role || "—"}</td>
                    <td>{h.security || "—"}</td>
                    <td>{h.direct ? "Directly" : "Indirectly"}</td>
                    <td>{fmtNumber(h.shares, 0)}</td>
                    <td>{pct === null ? "—" : fmtPct(pct, 3)}</td>
                    <td>{data.price ? fmtNumber(h.shares * data.price, 0) : "—"}</td>
                    <td>{fmtLongDate(h.as_of)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="co-hint">
            Positions reported by company insiders (officers, directors, 10% owners) in their latest SEC Form 4.
            Institutional shareholders (funds) are not shown yet.
          </p>
        </div>
      )}

      {!empty && view === "insiders" && (
        <div className="card co-card co-table-wrap">
          <table className="co-table co-table-left">
            <thead>
              <tr><th>Date</th><th>Insider</th><th>Role</th><th>Type</th><th>Shares</th><th>Price</th><th>Value</th><th>Held after</th></tr>
            </thead>
            <tbody>
              {data.trades.map((t, i) => (
                <tr key={i}>
                  <td>{fmtLongDate(t.date)}</td>
                  <td>{t.insider}</td>
                  <td>{t.role || "—"}</td>
                  <td className={t.code === "P" ? "change-up" : t.code === "S" ? "change-down" : ""}>{CODES[t.code] || t.code || "—"}</td>
                  <td>{fmtNumber(t.shares, 0)}</td>
                  <td>{t.price ? fmtMoney(t.price) : "—"}</td>
                  <td>{t.price && t.shares ? fmtMoney(amount(t), 0) : "—"}</td>
                  <td>{t.owned_after === null ? "—" : fmtNumber(t.owned_after, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="co-hint">From SEC Form 4 filings. Awards and tax withholding are routine; open-market buys and sells are the informative ones.</p>
        </div>
      )}
    </div>
  );
}
